import { NgClass, NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input, model, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@cmi/addin-core';
import { LoadingSpinner, Notification } from '@cmi/ui';
import { debounceTime, distinctUntilChanged } from 'rxjs';
import { DossierSearchService, MIN_SEARCH_LENGTH } from './dossier-search.service';
import { Dossier } from './dossier.models';

const DEBOUNCE_MS = 300;

/**
 * Searches the dossiers as the user types and lists the hits to pick from. The
 * search runs once typing pauses, and again when the tenant changes.
 * With nothing typed, it lists the proposals, if there are any.
 */
@Component({
  selector: 'cmi-dossier-search',
  imports: [NgClass, NgTemplateOutlet, TranslatePipe, LoadingSpinner, Notification],
  templateUrl: './dossier-search.html'
})
export class DossierSearch {
  /** What the user typed. Two-way, so the page can clear it for the next mail. */
  public readonly term = model<string>('');
  public readonly selectedGuid = input<string | null>(null);
  /** Shown while nothing is searched for. */
  public readonly proposals = input<Dossier[]>([]);
  public readonly proposalsLoading = input<boolean>(false);
  public readonly dossierSelect = output<Dossier>();

  /** The term once typing pauses: what was searched for. */
  protected readonly searchedTerm = toSignal(
    toObservable(this.term).pipe(debounceTime(DEBOUNCE_MS), distinctUntilChanged()),
    { initialValue: '' }
  );

  protected readonly results = inject(DossierSearchService).search(this.searchedTerm);

  /**
   * The search loads, or the proposals do where they stand in for the hits:
   * with no hits and no error.
   */
  protected readonly loading = computed(
    () =>
      this.results.loading() ||
      (this.proposalsLoading() && !this.results.failed() && this.results.dossiers() === undefined)
  );

  protected readonly searchTermTooShort = computed(() => {
    const term = this.searchedTerm();
    return term.length > 0 && term.length < MIN_SEARCH_LENGTH;
  });

  protected onReload(): void {
    this.results.reload();
  }

  protected onSearchInput(event: Event): void {
    this.term.set((event.target as HTMLInputElement).value);
  }

  protected isSelected(dossier: Dossier): boolean {
    return this.selectedGuid() === dossier.guid;
  }
}
